import type { GameManifest } from "./manifest.js";
import type { MatchOutcome } from "./rules.js";

/**
 * What every play reports, in rooms and solo alike (P5-AN-04, docs/metrics.md "Plays"): so a new
 * game is counted from its first day with no analytics change.
 */

/** Together, everyone for themselves, or two against two (ADR 0028). */
export type PlayMode = "coop" | "ffa" | "2v2";

/** A mission's mode: a battle mission is ffa, or 2v2 when the players have sides. */
export function playMode(manifest: GameManifest, missionId: string, teams: boolean): PlayMode {
  const mission = manifest.missions.find((m) => m.id === missionId);
  if (!mission?.battle) return "coop";
  return teams && mission.teams ? "2v2" : "ffa";
}

/** The properties on every play's start and end event. */
export function playProps(
  manifest: GameManifest,
  missionId: string,
  teams: boolean,
): { game_id: string; game_version: string; mission_id: string; game_mode: PlayMode } {
  return {
    game_id: manifest.id,
    game_version: manifest.version,
    mission_id: missionId,
    game_mode: playMode(manifest, missionId, teams),
  };
}

/** Stats the platform knows by name in every game's outcome (they keep their own props). */
const KNOWN = ["stars", "missions"] as const;

/**
 * An outcome's properties for the end event: the result, `stars` and `missions` when the game
 * reports them, and each of the manifest's `outcomeStats` as `stat_<name>`. Nothing undeclared.
 */
export function outcomeProps(
  manifest: GameManifest,
  outcome: MatchOutcome,
): Record<string, string | number> {
  const props: Record<string, string | number> = { result: outcome.result };
  for (const name of KNOWN) {
    const value = outcome.stats[name];
    if (typeof value === "number" && Number.isFinite(value)) props[name] = value;
  }
  for (const name of manifest.outcomeStats ?? []) {
    const value = outcome.stats[name];
    if (typeof value === "number" && Number.isFinite(value)) props[`stat_${name}`] = value;
  }
  return props;
}
